import { z } from 'zod';
import type { SignalStageType, SignalStatusType } from './generated';
import {
  SIGNAL_STATUS_VALUES,
  SignalStage,
  SignalStatus,
} from './signal-lifecycle.enum';

export { SignalStage, SignalStatus } from './signal-lifecycle.enum';

type SignalStatusValue = (typeof SIGNAL_STATUS_VALUES)[number];

export const StageSchema = z.enum([
  'discovered',
  'analyzed',
  'flagged',
  'confirmed',
]);

export type StageValue = z.infer<typeof StageSchema>;

export const ConfirmedVulnerabilityFindingStateSchema = z.enum([
  'open',
  'closed',
]);

export type ConfirmedVulnerabilityFindingStateType = 'open' | 'closed';

export const ALLOWED_STATUSES_BY_STAGE: Record<
  SignalStageType,
  readonly (SignalStatusType | null)[]
> = {
  [SignalStage.NEW]: [
    null,
    SignalStatus.PENDING_ENRICHMENT,
    SignalStatus.FAILED_ENRICHMENT,
  ],
  [SignalStage.IN_TESTING]: [
    null,
    SignalStatus.POTENTIAL_VULNERABILITY,
    SignalStatus.BLOCKED,
    SignalStatus.CONFIRMED_VULNERABILITY,
    SignalStatus.DISPROVEN_VULNERABILITY,
    SignalStatus.DUPLICATE,
  ],
  [SignalStage.NEEDS_REVIEW]: [
    null,
    SignalStatus.POTENTIAL_VULNERABILITY,
    SignalStatus.BLOCKED,
    SignalStatus.CONFIRMED_VULNERABILITY,
  ],
  [SignalStage.TESTED]: [
    SignalStatus.DISPROVEN_VULNERABILITY,
    SignalStatus.DUPLICATE,
    SignalStatus.CONFIRMED_VULNERABILITY,
    SignalStatus.BLOCKED,
  ],
};

export const DEFAULT_STAGE_BY_STATUS: Record<
  SignalStatusValue,
  SignalStageType
> = {
  [SignalStatus.POTENTIAL_VULNERABILITY]: SignalStage.NEEDS_REVIEW,
  [SignalStatus.BLOCKED]: SignalStage.NEEDS_REVIEW,
  [SignalStatus.CONFIRMED_VULNERABILITY]: SignalStage.NEEDS_REVIEW,
  [SignalStatus.DISPROVEN_VULNERABILITY]: SignalStage.TESTED,
  [SignalStatus.DUPLICATE]: SignalStage.TESTED,
  // Still gathering evidence - not yet in front of a human reviewer.
  [SignalStatus.PENDING_ENRICHMENT]: SignalStage.NEW,
  // Tried everything available; no definitive exploit verdict.
  [SignalStatus.FAILED_ENRICHMENT]: SignalStage.NEW,
};

export function defaultStageForSignalStatus({
  status,
}: {
  status: SignalStatusType | null;
}): SignalStageType {
  if (status === null) {
    return SignalStage.NEW;
  }

  return DEFAULT_STAGE_BY_STATUS[status];
}

export function isAllowedStageStatusCombination({
  stage,
  status,
}: {
  stage: SignalStageType;
  status: SignalStatusType | null;
}): boolean {
  return ALLOWED_STATUSES_BY_STAGE[stage].includes(status);
}

export function validateStageStatusCombination({
  stage,
  status,
}: {
  stage: SignalStageType;
  status: SignalStatusType | null;
}): { valid: true } | { valid: false; error: string } {
  if (isAllowedStageStatusCombination({ stage, status })) {
    return { valid: true };
  }

  const allowed = ALLOWED_STATUSES_BY_STAGE[stage]
    .map((value) => value ?? 'null')
    .join(', ');

  return {
    valid: false,
    error: `Invalid stage/status combination: ${stage}/${status ?? 'null'}. Allowed statuses for ${stage}: ${allowed}`,
  };
}

export const OPEN_CONFIRMED_VULNERABILITY_FINDING_STATUSES = [
  'Unresolved',
  'InProgress',
  'Retest',
] as const;

export type ConfirmedVulnerabilityFindingSnapshot = {
  status: string;
  isPublished?: boolean | null;
};

export function deriveConfirmedVulnerabilityFindingState({
  stage,
  findings,
}: {
  stage: SignalStageType;
  findings: ConfirmedVulnerabilityFindingSnapshot[];
}): ConfirmedVulnerabilityFindingStateType | null {
  if (stage !== SignalStage.TESTED) {
    return null;
  }

  const publishedFindings = findings.filter(
    (finding) => finding.isPublished === true,
  );

  if (publishedFindings.length === 0) {
    return null;
  }

  const hasOpen = publishedFindings.some((finding) =>
    OPEN_CONFIRMED_VULNERABILITY_FINDING_STATUSES.some(
      (openStatus) => openStatus === finding.status,
    ),
  );

  if (hasOpen) {
    return 'open';
  }

  return 'closed';
}

export type SignalStageStatusPair = {
  stage: SignalStageType;
  status: SignalStatusType | null;
};

export const EMPTY_FUNNEL_STAGE_COUNTS: Record<StageValue, number> = {
  [StageSchema.enum.discovered]: 0,
  [StageSchema.enum.analyzed]: 0,
  [StageSchema.enum.flagged]: 0,
  [StageSchema.enum.confirmed]: 0,
};

export function funnelStageValueFromStageStatus({
  stage,
  status,
}: SignalStageStatusPair): StageValue | null {
  if (stage === SignalStage.NEW || stage === SignalStage.IN_TESTING) {
    return StageSchema.enum.discovered;
  }

  if (stage === SignalStage.NEEDS_REVIEW && status === SignalStatus.BLOCKED) {
    return StageSchema.enum.analyzed;
  }

  if (stage === SignalStage.NEEDS_REVIEW) {
    return StageSchema.enum.flagged;
  }

  if (stage === SignalStage.TESTED) {
    if (status === SignalStatus.CONFIRMED_VULNERABILITY) {
      return StageSchema.enum.confirmed;
    }

    if (
      status === SignalStatus.DISPROVEN_VULNERABILITY ||
      status === SignalStatus.DUPLICATE ||
      status === SignalStatus.BLOCKED
    ) {
      return StageSchema.enum.analyzed;
    }
  }

  return null;
}

export function buildFunnelStageCounts({
  groups,
}: {
  groups: Array<SignalStageStatusPair & { count: number }>;
}): Record<StageValue, number> {
  const counts = { ...EMPTY_FUNNEL_STAGE_COUNTS };

  groups.forEach(({ stage, status, count }) => {
    const funnelStage = funnelStageValueFromStageStatus({ stage, status });

    if (funnelStage) {
      counts[funnelStage] += count;
    }
  });

  return counts;
}
